import { Badge } from '@repo/ui/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@repo/ui/components/ui/card'
import { Switch } from '@repo/ui/components/ui/switch'
import { cn } from '@repo/ui/lib/utils'
import { CalendarDays, PenLine } from 'lucide-react'
import { toast } from 'sonner'
import type { CalendarConnectionSummary } from '@/lib/calendar/types'
import { trpc } from '@/lib/trpc'

interface CalendarSourceListProps {
  connections: CalendarConnectionSummary[]
}

export function CalendarSourceList({ connections }: CalendarSourceListProps) {
  const utils = trpc.useUtils()
  const updateMutation = trpc.calendar.sources.updateSelection.useMutation({
    onSuccess: async () => {
      await utils.calendar.connections.list.invalidate()
    },
    onError: (error) =>
      toast.error('Could not update calendar source', { description: error.message }),
  })
  const sources = connections.flatMap((connection) =>
    connection.sources.map((source) => ({ ...source, provider: connection.provider }))
  )

  return (
    <Card className="rounded-[6px] border-border/70 bg-background shadow-none">
      <CardHeader className="p-4">
        <CardTitle className="text-base">Calendar sources</CardTitle>
        <p className="text-sm text-muted-foreground">
          Imported busy events block public booking. One calendar can receive Zippity updates.
        </p>
      </CardHeader>
      <CardContent className="px-4 pt-0 pb-4">
        {sources.length === 0 ? (
          <div className="rounded-[6px] border border-dashed border-border/70 bg-muted/20 p-4 text-sm text-muted-foreground">
            Connect Google Calendar or Outlook to choose calendars.
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {sources.map((source) => (
              <div
                key={source.id}
                className="grid gap-3 rounded-[6px] border border-border/70 bg-card/40 p-3 sm:grid-cols-[minmax(0,1fr)_auto_auto]"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span
                    className={cn(
                      'h-8 w-1.5 shrink-0 rounded-full bg-primary',
                      !source.importEnabled && 'bg-muted-foreground/35'
                    )}
                    style={source.color ? { backgroundColor: source.color } : undefined}
                  />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">{source.name}</div>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {source.isPrimary ? <Badge variant="outline">Primary</Badge> : null}
                      {source.timezone ? (
                        <Badge variant="secondary">{source.timezone}</Badge>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-2 text-sm">
                  <span className="flex items-center gap-1.5 text-muted-foreground">
                    <CalendarDays className="h-3.5 w-3.5" />
                    Import
                  </span>
                  <Switch
                    checked={source.importEnabled}
                    disabled={updateMutation.isPending}
                    onCheckedChange={(checked) =>
                      updateMutation.mutate({ sourceId: source.id, importEnabled: checked })
                    }
                  />
                </div>

                <div className="flex items-center justify-between gap-2 text-sm">
                  <span className="flex items-center gap-1.5 text-muted-foreground">
                    <PenLine className="h-3.5 w-3.5" />
                    Write
                  </span>
                  <Switch
                    checked={source.writeDestination}
                    disabled={updateMutation.isPending}
                    onCheckedChange={(checked) =>
                      updateMutation.mutate({ sourceId: source.id, writeDestination: checked })
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
